import { action, computed, IObservableArray, observable, toJS } from "mobx";
import { observer, Provider } from "mobx-react";
import * as qs from "query-string";
import * as React from "react";
import { Route, RouteComponentProps } from "react-router-dom";
import { AddButton } from "../Buttons/AddButton";
import { ButtonGroup } from "../Buttons/ButtonGroup";
import { DeleteButton } from "../Buttons/DeleteButton";
import { MoveButton } from "../Buttons/MoveButton";
import { SearchNextButton } from "../Buttons/Pdf/SearchNextButton";
import { SearchPreviousButton } from "../Buttons/Pdf/SearchPreviousButton";
import { ResultSetSizeButton } from "../Buttons/ResultSetSizeButton";
import { SelectionButton } from "../Buttons/SelectionButton";
import * as DocumentManagement from "../DocumentManagement";
import { Area } from "../General/Area";
import { AreaAside } from "../General/AreaAside";
import { Toolbar, ToolbarModus } from "../General/Toolbar";
import { TypeAheadSearchInput } from "../General/TypeAheadSearchInput";
import { WizardBox, WizardBoxType } from "../General/WizardBox";
import { AppStore } from "../Stores/AppStore";
import { CollectionStoreImpl } from "../Stores/CollectionStore";
import { DialogStore } from "../Stores/DialogStore";
import { DocumentStoreImpl } from "../Stores/DocumentStore";
import { Strings } from "../Stores/StringsStore";
import { UiRepos } from "../Stores/UiStore";
import { Urls } from "../Urls";
import { format, newGuid } from "../Utilities";
import { CollectionEdit } from "./CollectionEdit";
import { CollectionTree } from "./CollectionTree";
import { DocumentEdit } from "./DocumentEdit";
import { FileEdit } from "./FileEdit";



interface ICollectionManagementRouteProps extends RouteComponentProps<any> { }

@observer
export class Collections extends React.Component<ICollectionManagementRouteProps, {}> {

	store: CollectionStoreImpl = new CollectionStoreImpl();
	documentStore: DocumentStoreImpl = new DocumentStoreImpl();
	wasCollapsed: boolean;
	@observable resultCount: number = -1;

	componentDidMount() {
		this.loadUiSettings();
		this.setTitle();
		window.addEventListener('orientationchange', this.handleOrientation);
	}

	componentDidUpdate() {
		if (!this.props.children && !this.store.collection) {
			this.setTitle();
		}
	}

	componentWillUnmount() {
		this.store.asideTreeViewStore.currentResult = null;
		window.removeEventListener("orientationchange", this.handleOrientation);
	}

	setTitle = () => {
		AppStore.setTitle(Strings.documentManagement.collections.title);
	}

	private loadUiSettings = () => {
		const parsedQuery = qs.parse(this.props.location.search);
		if (parsedQuery && parsedQuery.treepath) {
			let collections: string[] = parsedQuery.treepath.split(",");
			this.store.asideTreeViewStore.treePath.replace(collections.filter(function (e) { return e }));
			this.store.areaUiInfo.treePath = toJS(this.store.asideTreeViewStore.treePath);
			let collection = collections[collections.length - 1];
			this.store.asideTreeViewStore.currentFocusedCollection = collection;
		}
		this.store.currentCollections = UiRepos.collectionUiInfo.focus;
		this.store.areaUiInfo.isCollapsed = UiRepos.collectionUiInfo.isCollapsed;
		this.store.areaUiInfo.asideWidth = UiRepos.collectionUiInfo.asideWidth;
		this.store.areaUiInfo.contentGroupAccordionStatus.replace(UiRepos.collectionUiInfo.contentGroupAccordionStatus);
	}

	@action getSearchResultCount = () => {
		this.resultCount = -1;
		if (this.store.searchTerm) {
			DocumentManagement.queryCollectionCountAsync(this.store.searchTerm).then(result => {
				this.resultCount = result;
				this.handleSearchNext(null);
			}).catch(error => {
				DialogStore.showError(Strings.general.errorDialogTitle, error);
			});
		}
		else {
			this.store.asideTreeViewStore.collapseCollections(this.store.asideTreeViewStore.model);
			this.store.asideTreeViewStore.currentResult = null;
		}
	}

	@action handleSearchNext = (currentResultForQuery: string) => {
		if (this.store.searchTerm) {
			let query = new DocumentManagement.CollectionsQuery();
			query.currentResult = currentResultForQuery;
			query.searchTerm = this.store.searchTerm;
			DocumentManagement.queryNextCollectionAsync(query).then(result => {
				if (result.length === 0) {
					this.store.asideTreeViewStore.collapseCollections(this.store.asideTreeViewStore.model);
					this.store.asideTreeViewStore.currentResult = null;
				}
				else if (this.store.asideTreeViewStore.currentResult !== result[result.length - 1]) {
					this.store.asideTreeViewStore.collapseCollections(this.store.asideTreeViewStore.model);
					this.store.asideTreeViewStore.currentResult = result[result.length - 1];
					let collections = this.store.asideTreeViewStore.model.subCollections;
					this.nextNode(result, collections, 0);
					AppStore.keepOnPage(`${Urls.collections}/${result[result.length - 1]}?treepath=${result.slice(0, result.length - 1).join(",")}`);
				}
			}).catch(error => {
				DialogStore.showError(Strings.general.errorDialogTitle, error);
			});
		}
		else {
			this.store.asideTreeViewStore.collapseCollections(this.store.asideTreeViewStore.model);
			this.store.asideTreeViewStore.currentResult = null;
		}
	}

	@action handleSearchPrevious = () => {
		if (this.store.searchTerm) {
			let query = new DocumentManagement.CollectionsQuery();
			query.currentResult = this.store.asideTreeViewStore.currentResult;
			query.searchTerm = this.store.searchTerm;
			DocumentManagement.queryPreviousCollectionAsync(query).then(result => {
				if (result.length === 0) {
					this.store.asideTreeViewStore.collapseCollections(this.store.asideTreeViewStore.model);
					this.store.asideTreeViewStore.currentResult = null;
				}
				else if (this.store.asideTreeViewStore.currentResult !== result[result.length - 1]) {
					this.store.asideTreeViewStore.collapseCollections(this.store.asideTreeViewStore.model);
					this.store.asideTreeViewStore.currentResult = result[result.length - 1];
					let collections = this.store.asideTreeViewStore.model.subCollections;
					this.nextNode(result, collections, 0);
					AppStore.keepOnPage(`${Urls.collections}/${result[result.length - 1]}?treepath=${result.slice(0, result.length - 1).join(",")}`);
				}
			}).catch(error => {
				DialogStore.showError(Strings.general.errorDialogTitle, error);
			});
		}
		else {
			this.store.asideTreeViewStore.collapseCollections(this.store.asideTreeViewStore.model);
			this.store.asideTreeViewStore.currentResult = null;
		}
	}

	nextNode(result: string[], children: IObservableArray<DocumentManagement.CollectionInfo>, index: number) {
		if (index < result.length - 1) {
			let collection = children.filter(c => c.guid === result[index])[0];
			collection.isExpanded = true;
			DocumentManagement.getSubCollectionsAsync(collection.guid).then(subCollections => {
				collection.subCollections = observable.array(subCollections);
				this.nextNode(result, collection.subCollections, index + 1);
			});
		}
	}

	@computed get collectionLink() {
		return `${Urls.collections}/${this.store.asideTreeViewStore.model.guid}?treepath=${this.store.asideTreeViewStore.treePath.filter(function (e) { return e }).join(",")}`;
	}

	handleAddCollection = () => {
		let parentIdSegment = "", newCollectionUrl = "";
		if (this.store.currentCollections && (!this.store.collection || !this.store.collection.isNew)) {
			parentIdSegment = `${this.store.currentCollections}/`;
		}
		newCollectionUrl = `${Urls.collections}/${parentIdSegment}${newGuid()}`;
		AppStore.keepOnPage(newCollectionUrl, true);
		this.store.areaUiInfo.selectCollapse();
	}

	handleOrientation = () => {
		if (screen.width > screen.height) {
			if (screen.width >= 880 && this.store.areaUiInfo.isCollapsed) {
				this.store.areaUiInfo.isCollapsed = false;
				this.wasCollapsed = true;
			}
		}
		else if (!this.store.areaUiInfo.isCollapsed && this.wasCollapsed) {
			this.store.areaUiInfo.isCollapsed = true;
			this.wasCollapsed = false;
		}
	}

	@computed get treeViewClass(): string {
		return this.store.asideTreeViewStore.inSelectionMode ? "treeview is--selection" : "treeview";
	}

	handleSearchTermChangeFromTypeAhead = (searchTerm: string) => {
		this.store.searchTerm = searchTerm;
		clearTimeout(this.store.searchTimeoutHandle);
		this.store.searchTimeoutHandle = setTimeout(() => {
			this.getSearchResultCount();
		}, 300);
	}

	render() {
		let resultCountText = this.resultCount === 1 ? Strings.documentManagement.collections.resultSetSizeTextSingle : this.resultCount === -1 ? Strings.reader.searchingText : Strings.documentManagement.collections.resultSetSizeTextMultiple;
		//for selection mode set the class is--selection at the tree view root node.
		let noCollapse = this.props.children === null || (!this.store.collection && !this.documentStore.model);
		let aside = <AreaAside noCollapse={noCollapse}>
			<Toolbar modus={ToolbarModus.Search}>
				<TypeAheadSearchInput showSuggestionList={false} searchTermChangeCallback={this.handleSearchTermChangeFromTypeAhead} buttonAlignment={3} />
				<ButtonGroup>
					<SelectionButton title={Strings.documentManagement.collections.selectionButtonTooltip} onClick={this.store.asideTreeViewStore.toggleSelectionMode} pressed={this.store.asideTreeViewStore.inSelectionMode} testId="selectionButton" />
					<AddButton title={Strings.documentManagement.collections.addButtonTooltip} onClick={this.handleAddCollection} hide={this.store.asideTreeViewStore.inSelectionMode} testId="addButton" />
					<DeleteButton title={Strings.documentManagement.collections.deleteButtonTooltip} hide={!this.store.asideTreeViewStore.inSelectionMode} disabled={!this.store.hasSelectedCollections} onClick={this.store.handleDelete} testId="deleteButton" />
					<MoveButton title={Strings.documentManagement.collections.moveButtonTooltip} hide={!this.store.asideTreeViewStore.inSelectionMode} disabled={!this.store.hasSelectedCollections} onClick={this.store.collectionMoveWizardState.open} testId="moveButton" />
				</ButtonGroup>
			</Toolbar>
			<div className="area-grid__aside-content">
				{this.store.searchTerm ? <div className="collections-searchResult">
					<ResultSetSizeButton onClick={this.store.areaUiInfo.selectCollapse} label={format(resultCountText, this.resultCount)} testId="resultSetSizeButton" removeHover={true} />
					<SearchPreviousButton title={Strings.documentManagement.collections.findPreviousButtonTooltip} onClick={this.handleSearchPrevious} testId="findPreviousButton" disabled={this.resultCount === 0 || !this.store.asideTreeViewStore.currentResult} />
					<SearchNextButton title={Strings.documentManagement.collections.findNextButtonTooltip} onClick={() => this.handleSearchNext(this.store.asideTreeViewStore.currentResult)} testId="findNextButton" disabled={this.resultCount === 0 || !this.store.asideTreeViewStore.currentResult} />
				</div> : null}
				<CollectionTree treeViewStore={this.store.asideTreeViewStore} testId="collectionTree" />
			</div>
			{
				this.store.collectionMoveWizardState.isOpen ? <WizardBox context={this.store.collectionMoveWizardState} type={WizardBoxType.ConfirmReject}>
					<div className="wizardbox-content no--toolbar">
						<div className="wizardbox-module">
							<CollectionTree treeViewStore={this.store.moveTreeViewStoreCollections} testId="wizardCollectionTree" />
						</div>
					</div>
				</WizardBox> : null
			}
		</AreaAside>;
		return <Provider store={this.store} documentStore={this.documentStore} areaUiInfo={this.store.areaUiInfo}>
			<Area asideComponent={aside}>
				<Route exact path={`${this.props.match.url}/:id`} component={CollectionEdit} />
				<Route exact path={`${this.props.match.url}/:parrentId/:id`} component={CollectionEdit} />
				<Route exact path={`${this.props.match.url}/:collection/documents/:id`} component={DocumentEdit} />
				<Route exact path={`${this.props.match.url}/:collection/documents/:document/files/:language`} component={FileEdit} />
				<Route exact path={`${this.props.match.url}/:collection/documents/:document/files`} component={FileEdit} />
			</Area>
		</Provider>;
	}
}